import { ProblemAlert } from '@/components/ProblemAlert';
import { WarningsList } from '@/components/WarningsList';
import { Button } from '@/components/ui/button';
import { SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { EnvVarDetails } from './EnvVarDetails';
import { EnvVarKeyField } from './EnvVarKeyField';
import { EnvVarScopeFields } from './EnvVarScopeFields';
import { EnvVarTypeConfig } from './EnvVarTypeConfig';
import { EnvVarVisibility } from './EnvVarVisibility';
import { ValueEditor } from './ValueEditor';
import { useEnvVarForm, type EnvVarFormProps } from './useEnvVarForm';

function EnvVarHeader({ edit, keyName }: { edit: boolean; keyName: string | undefined }) {
  return (
    <SheetHeader>
      <SheetTitle>{edit ? `Edit ${keyName}` : 'Add variable'}</SheetTitle>
      <SheetDescription id="env-var-desc" className="text-xs">
        {edit ? 'Metadata only — the value changes through a new version.' : 'The value is encrypted on save and only leaves Hangar towards a build.'}
      </SheetDescription>
    </SheetHeader>
  );
}

function EnvVarFooter({ edit, pending, onDone }: { edit: boolean; pending: boolean; onDone: () => void }) {
  return (
    <SheetFooter className="flex-row justify-end border-t">
      <Button type="button" variant="ghost" onClick={onDone}>
        Cancel
      </Button>
      <Button type="submit" data-testid="env-var-submit" loading={pending}>
        {edit ? 'Save metadata' : 'Create variable'}
      </Button>
    </SheetFooter>
  );
}

/** The sheet's form: header, scope (new only), key, visibility, type, value (new only), details, footer. */
export function EnvVarForm(props: EnvVarFormProps) {
  const form = useEnvVarForm(props);
  const { edit, values } = form;
  return (
    <form data-testid="env-var-form" onSubmit={(e) => void form.onSubmit(e)} noValidate className="flex h-full min-h-0 flex-col" aria-label={edit ? 'Edit variable' : 'Add variable'}>
      <EnvVarHeader edit={edit} keyName={props.editing?.key} />
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-5">
        {!edit ? <EnvVarScopeFields form={form} apps={props.apps} environments={props.environments} /> : null}
        <EnvVarKeyField form={form} />
        <EnvVarVisibility form={form} />
        <EnvVarTypeConfig form={form} />
        {!edit ? <ValueEditor id="env-var-value" valueType={values.valueType} state={values.value} onChange={(value) => form.patch({ value })} onValueTypeChange={(valueType) => form.patch({ valueType })} error={form.server.value} required submitted={form.submitted} /> : null}
        <EnvVarDetails form={form} />
        <WarningsList warnings={[]} />
        <ProblemAlert error={form.problem} testId="env-var-problem" />
      </div>
      <EnvVarFooter edit={edit} pending={form.pending} onDone={props.onDone} />
    </form>
  );
}
